/**
 * applyWorkspaceLangPolicy — re-resolve the session language for the workspace
 * that is active NOW.
 *
 * ── Why a second resolution point exists ──────────────────────────────────
 * bootShell resolves the language ONCE, at boot, for the workspace that was
 * active then (step 7b). A workspace-enforced language
 * (`settings.languagePolicy: 'enforced'`) is the first level of that
 * precedence that can CHANGE WITHOUT A RELOAD: it belongs to a workspace, not
 * to the person, so switching workspaces changes the answer. Without this,
 * enforcement only ever applied to whichever workspace happened to be active
 * at boot — a member who switched INTO an enforced workspace kept their own
 * language, and one who switched OUT of it stayed stuck in the workspace's.
 *
 * ── Idempotent, and safe to call on any workspace event ───────────────────
 * It recomputes the WHOLE precedence (not just the enforced level) and does
 * nothing when the answer has not changed. So it is correct on a switch, on a
 * late-arriving workspace record, and on a redundant re-fire — which is why
 * its caller can subscribe to `onActiveWorkspaceFull` and stop reasoning about
 * which switch path it came through. There are four of them (the shell's
 * `sdk.workspaceSwitched` handler, `switchOrg`, the cross-tab storage echo,
 * and the boot chain's own completion), and they all funnel through
 * `setActiveWorkspaceRecord`, which is what publishes that event.
 *
 * ── LEAVING a workspace restores that workspace's own answer ──────────────
 * That falls out of recomputing the whole chain rather than being a special
 * case: the next workspace's own `settings.language` (else the brand's, else
 * English) decides. Nothing was written to the account while the member was inside, so
 * there is nothing to undo — see `setLang`'s `transient` contract, which this
 * function always uses.
 *
 * ── The SAME language can still owe its text sets (KA-1) ──────────────────
 * After an in-page sign-in the language may not change at all (a Georgian
 * browser, a Georgian workspace), while the signed-out boot only DECLARED the
 * shell's namespaces and loaded none of them. Then this calls `setLang` once
 * for the same language — only for a lazily-loaded language, and only while a
 * declared namespace is still missing. See the body.
 *
 * ── FA204 / frank trap ───────────────────────────────────────────────────
 * `functions/` is a frank section barrel: every export becomes its own emitted
 * entry, and a bare module-scope or cross-barrel read from inside a serialized
 * export is unresolvable in the eval-revived runtime. So the enforced-pair
 * test is INLINED here (the same shape `setLang.js` and `languages.js` use for
 * their own lists), and the two siblings this needs at runtime — `resolveLang`
 * and `setLang` — are reached through `el.call`, the sanctioned resolver
 * (Rule 8 / FA001), never an import.
 *
 * A `function` declaration, not an arrow, for the same reason `setLang` is
 * one: `el.call('applyWorkspaceLangPolicy')` invokes it with `this` bound to
 * the element, and an arrow cannot receive that binding.
 *
 * @param {object} [el] - the element; omitted on the `el.call(...)` path,
 *                        where `this` is the element already
 * @returns {Promise<boolean>} true when `setLang` ran: the session language
 *                             changed, or the same language loaded the text
 *                             sets it still owed
 */
export async function applyWorkspaceLangPolicy(el) {
  const element =
    el && typeof el.call === 'function' ? el : this && typeof this.call === 'function' ? this : null
  if (!element) {
    console.warn(
      "[applyWorkspaceLangPolicy] no element — call it as el.call('applyWorkspaceLangPolicy') " +
        'or pass the element. The workspace language policy was NOT applied.'
    )
    return false
  }

  const root = element.state?.root || element.state || element.context?.state || null
  if (!root) return false

  // A record still IN FLIGHT is not an answer. A list/claims-shaped workspace
  // (an id, no `settings`, not stamped full, completion not marked
  // unreachable — the `pending` state of workspaceSettings.js) carries no
  // language at all, so resolving it reads "set to nothing" and paints
  // English; then the full record lands and paints the workspace's real
  // language. That is a second language paint on every warm boot and on every
  // switch into a Georgian workspace. `onActiveWorkspaceFull` re-runs this
  // function on the full record, so skipping here loses nothing. `setLang`'s
  // gate holds the active language for the same window (see its body).
  // Inlined, not read off workspaceSettings.js — same FA204 trap as below.
  const _ws = root.workspace && typeof root.workspace === 'object' ? root.workspace : null
  if (
    _ws &&
    !(_ws.settings && typeof _ws.settings === 'object') &&
    _ws._shape !== 'full' &&
    !!(_ws.id || _ws._id) &&
    !_ws._completionUnreachable
  ) {
    return false
  }

  const settings = root.workspace?.settings || null
  const SUPPORTED = ['en', 'ka']
  const enforced =
    settings &&
    settings.languagePolicy === 'enforced' &&
    typeof settings.language === 'string' &&
    SUPPORTED.indexOf(settings.language) !== -1
      ? settings.language
      : null

  // The SAME precedence bootShell applies, from the SAME function, so the two
  // resolution points cannot drift. Anything absent is simply skipped. The
  // member's account preference and this device's `localStorage.lang` are no
  // longer inputs (Nika 2026-09-10: the workspace's own language decides, a
  // workspace set to nothing reads English) — see resolveLang.
  const next = element.call('resolveLang', {
    workspaceEnforced: enforced,
    workspaceDefault: settings?.language || null,
    brandDefault: root.whitelabelBrand?.lang || null
  })

  const active = root.lang || element.context?.state?.lang || null
  if (!next) return false
  if (next === active) {
    // THE SAME LANGUAGE, BUT ITS TEXT SETS ARE NOT LOADED YET (KA-1). A
    // signed-out boot loads only `common` + `auth` and DECLARES the shell's
    // namespaces without loading them (bootResolve, "declare only"). A person
    // who then signs in on the page, into a workspace in the language already
    // active, changes no language — so without this nothing ever loads
    // `ka/shell`, `ka/canvas`, … and most of the shell stays English until a
    // reload. One `setLang` loads every declared namespace (its "Bundles"
    // union) and records each as asked for, so the next fire finds nothing
    // missing and costs nothing.
    //
    // Missing = declared, not yet asked for, and served for this language. A
    // namespace with no bundle for it would only fall back to English. A host
    // with no registry (frank's revived runtime) serves nothing, so it never
    // loops. `getLoadedNamespaces` not reachable -> no answer, nothing forced.
    const _loaded = element.call('getLoadedNamespaces')
    if (!Array.isArray(_loaded)) return false
    const _declared = ['common', ...(element.context?.polyglot?.namespaces || [])]
    const _missing = _declared.some(
      (ns) => _loaded.indexOf(ns) === -1 && element.call('hasI18nBundle', next, ns) === true
    )
    if (!_missing) return false
    // A BUNDLED language (English) carries every namespace already: there is
    // nothing to load, and its `setLang` records nothing, so forcing one here
    // would repeat on every workspace event. Read last: it merges the base.
    const _static = element.call('i18nStaticTranslations')
    if (_static && _static[next]) return false
  }

  // `transient` — this language was RESOLVED, never chosen. It is persisted
  // NOWHERE: not to `localStorage`, not to the account. Persisting it would
  // let a workspace's rule impersonate the member's own choice and follow them
  // out of the workspace that made it (see setLang's header).
  //
  // `redeliver` — the tree is already mounted here, unlike at boot. Without it
  // the bundles land but nothing re-pushes them, so already-rendered props
  // keep the language they were born with.
  return element.call('setLang', next, { transient: true, redeliver: true })
}
